





















































































































import _ from 'lodash'
import Component, {mixins} from 'vue-class-component'
import {VDataTableHeader, VDataTableOptions} from '@/types'
import {State} from 'vuex-class'
import {GetReleaseLogInput, ReleaseLogsList} from '@/modules/app-router/api'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import {DateMixin} from '@/modules/core/date'
import ReleaseLog, {LogLevel} from '@/modules/app-router/model/ReleaseLog'
import AppReleaseSelect from '@/modules/app-router/components/AppRelease/AppReleaseSelect.vue'
import UserSelect from '@/modules/user/components/UserSelect.vue'
import LogMixin from '@/modules/app-router/components/AppRelease/LogMixin'
import Wamp from '@/modules/core/wamp/mixins/Wamp'
import {ISubscription} from 'autobahn'

interface LogEventDataTableHeader extends VDataTableHeader {
    /**
     * Texto descriptivo utilizado para informar al usuario la finalidad
     * de la columna en la pantalla de configuración de campos
     * de la tabla
     */
    description: string
}

/**
 * Registro de eventos
 *
 * Pantalla que muestra al usuario los eventos que el sistema ha registrado
 * durante su funcionamiento.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {UserSelect, AppReleaseSelect},
})
export default class EventLog extends mixins(DateMixin, LogMixin, Wamp) {
    /**
     * Lista de registros de eventos generados por una versión en concreto
     * de la aplicación
     */
    @State(state => state.appRouter.releaseLogs)
    public logs: ReleaseLogsList

    @State(state => state.appRouter.getReleaseLogsInput)
    public input: GetReleaseLogInput

    /**
     * Ver en tiempo real
     *
     * Indica si la pantalla se encuentra mostrar los registros del sistema en
     * tiempo.
     */
    public watchLive = false
    public liveRecords: ReleaseLog[] = []

    /**
     * Lista de registros de eventos recibidos por el enrutador WAMP. A esta lista
     * se aplican los mismo filtros que a la paginación normal.
     */
    public get filteredLiveRecords () {
        // No se ha establecido ningún filtro de búsqueda, se retorna la lista
        // completa de registros de eventos
        if (this.inputIsEmpty) {
            return this.liveRecords
        }

        const input = this.input

        // Devuelve "true" si el nivel del registro coincide con el nivel argumentado
        const matchLevel = (record: ReleaseLog) => (input.levels && input.levels.length)
            ? input.levels.includes(record.levelName)
            : true

        // Devuelve "true" si el usuario que provoco el evento es el mismo al usuario
        // argumentado
        const matchUser = (record: ReleaseLog) => input.user
            ? input.user.email === _.get(record, 'context.user.email', '')
            : true

        const sinceDate = (record: ReleaseLog) => input.sinceDate ?
            record.datetime.getTime() >= input.sinceDate.getTime()
            : true

        const untilDate = (record: ReleaseLog) => input.untilDate
            ? record.datetime.getTime() <= input.untilDate.getTime()
            : true

        const matchRelease = (record: ReleaseLog) => {
            // El filtro no ha sido definido por el usuario
            if (!input.releases || !input.releases.length) {
                return true
            }

            const id = _.get(record, 'context.release.id')
            const version = _.get(record, 'context.release.version')

            const matched = input.releases.filter(release => release.id === id || release.version === version)

            return !!matched.length
        }

        return this.liveRecords.filter(record =>
            matchLevel(record)
            && matchUser(record)
            && sinceDate(record)
            && untilDate(record)
            && matchRelease(record),
        )
    }

    /**
     * Indica si el input que define los criterios de búsqueda de registros
     * se encuentra vacío y no se ha establecido ningún filtro
     * por parte del usuario.
     */
    public get inputIsEmpty () {
        const input = this.input

        return !input.user
            && (!input.levels || !input.levels.length)
            && (!input.releases || !input.releases.length)
            && !input.sinceDate
            && !input.untilDate
    }

    public logRegisterSubscription: ISubscription | null

    /**
     * Niveles de gravedad de los registros de eventos y sus correspondientes
     * nombres.
     */
    public logLevels = [
        { value: LogLevel.DEBUG, text: 'Depuración'},
        { value: LogLevel.INFO, text: 'Información'},
        { value: LogLevel.NOTICE, text: 'Notificar'},
        { value: LogLevel.WARNING, text: 'Advertencia'},
        { value: LogLevel.ERROR, text: 'Error'},
        { value: LogLevel.CRITICAL, text: 'Error crítico'},
        { value: LogLevel.ALERT, text: 'Alerta'},
        { value: LogLevel.EMERGENCY, text: 'Emergencia'},
    ]

    /**
     * Campos disponibles
     *
     * Lista con todos los campos que el usuario puede visualizar en la tabla
     * que muestra la lista de eventos del registro.
     *
     * El usuario puede elegir que campos mostrar.
     */
    public availableFields: LogEventDataTableHeader[] = [
        { value: 'id', text: 'Id', description: 'Identificador del registro' },
        { value: 'levelName', text: 'Nivel', description: 'Nivel gravedad del registro'},
        { value: 'datetime', text: 'Fecha', description: 'Fecha y hora'},
        { value: 'message', text: 'Mensaje', description: 'Mensaje'},
        { value: 'context.user.id', text: 'ID Usuario', description: 'ID del usuario que provoco el evento'},
        { value: 'context.user.name', text: 'Nombre de usuario', description: 'Nombre del usuario'},
        { value: 'context.session', text: 'Session', description: 'Sesión del enrutador'},
        {
            value: 'context.user.email',
            text: 'Email Usuario',
            description: 'Correo electrónico del usuario que provoco el evento',
        },
        {
            value: 'context.release.version',
            text: 'Versión',
            description: 'Número de versión de la distribución en la que ocurrió el evento',
        },
        {
            value: 'context.releaseId',
            text: 'Versión',
            description: 'Id de la distribución en la que ocurrió el evento',
        },
    ]

    /**
     * Método invocado cada vez que, desde el enrutador WAMP, se recibe
     * el evento "log-registered", el cual indica que un nuevo
     * registro ha sido añadido al sistema.
     *
     * Este método es utilizado para mostrar en tiempo real los registros.
     */
    public onLogRegistered (args: any) {
        let record = args[0] as any | undefined
        if (!record) {
            return
        }

        record = ReleaseLog.deserialize(record)
        this.liveRecords.push(record)
    }

    /**
     * Activa el modo de visualización de registros en tiempo.
     */
    public async enableWatchLiveMode () {
        await this.$wamp.connect()

        this.logRegisterSubscription = await this.$wamp.subscribe(
            'net.unigest.log-registered',
            this.onLogRegistered,
        )

        this.watchLive = true
    }

    /**
     * Desactiva el modo en tiempo real
     */
    public async disableWatchLiveMode () {
        if (this.logRegisterSubscription) {
            await this.logRegisterSubscription.unsubscribe()
            this.logRegisterSubscription = null
        }

        this.watchLive = false
    }

    /**
     * Limpia los filtros de búsqueda y solicita una nueva lista de
     * registros con los filtros por defecto
     */
    public async clearFilters () {
        this.$store.commit('appRouter/RESET_RELEASE_LOGS_INPUT')
        this.tableOptions.page = 1
        await this.loadLogs()
    }

    public updateLevelFilter = _.debounce(function (this: EventLog, levels: string[]) {
        this.updateFilter('levels', levels)
    }, 1000)

    public get headers (): VDataTableHeader[] {
        return this.availableFields.filter((field, index) => this.selectedFields.includes(index))
    }

    public tableOptions: Partial<VDataTableOptions> = {}
    public selectedFields: number[] = [1, 2, 3, 4, 5]
    public loading = false
    public lastPageRecords: KeyValuePair = {}
    public fieldConfiguration = false
    public userSelect = false

    /**
     * Solicita a la aplicación la carga de la lista de registros de eventos
     * de la versión actualmente mostrada al usuario
     */
    public async loadLogs () {
        // El usuario esta observando el registro de eventos en tiempo real
        // no es necesario actualizar la lista de registros desde el
        // servidor
        if (this.watchLive) {
            return
        }

        this.loading = true
        await this.$store.dispatch('appRouter/loadReleaseLogs')

        this.loading = false
    }

    /**
     * Método invocado cuando cualquier de los filtros de búsqueda es modificado.
     * Aplica el cambio en el objeto que será enviado a la API y solicita
     * al servidor una nueva lista de registros con los nuevos filtros.
     */
    public async updateFilter (name: string, value: any) {
        this.updateInput(name, value)
        // Resetea la paginación
        this.updateInput('since', null)

        await this.loadLogs()
    }

    /**
     * Método invocado cuando los filtros de la tabla cambía Solicita a la
     * aplicación la carga de una nueva paginación de registros
     * de eventos
     */
    public async loadTableRecords (options: VDataTableOptions) {
        this.loading = true

        if (options.page === 1) {
            this.updateInput('since', null)
        } else {
            this.updateInput('since', this.lastPageRecords[options.page - 1])
        }

        this.updateInput('limit', options.itemsPerPage)
        this.updateInput('sortBy', options.sortBy)
        this.updateInput('sortDesc', options.sortDesc)
        await this.loadLogs()

        if (this.logs.records.length) {
            this.lastPageRecords[options.page] = this.logs.records[this.logs.records.length - 1].id
        }

        this.loading = false
    }

    /**
     * Actualiza un campo del objeto de criterios de búsqueda de registros
     * de eventos
     *
     * @param field
     * @param value
     */
    public updateInput (field: string, value: any) {
        this.$store.commit('appRouter/UPDATE_RELEASE_LOGS_INPUT_FIELD', {
            field, value,
        })
    }

    public errorCaptured () {
        this.loading = false
    }
}
